import { message, Select } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useState } from "react";
import { AiOutlineDownload } from "react-icons/ai";
import { useQuery } from "react-query";
import { serverUrl } from "../../../../../nestserver";

const ReportDetails = () => {
  const getCurrentAcademicYear = () => {
    const today = new Date();
    const curYear = today.getFullYear();
    const curMonth = today.getMonth() + 1;
    const startYear = curMonth >= 6 ? curYear : curYear - 1;
    const endYearShort = String(startYear + 1).slice(-2);
    return `${startYear}-${endYearShort}`;
  };

  const academicYears = [getCurrentAcademicYear()];
  const { Option } = Select;
  const dateFormat = "YYYY";
  const [classId, setClassId] = useState(null);
  const [selectedYear, setSelectedYear] = useState(getCurrentAcademicYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loadingMonth, setLoadingMonth] = useState(null);

  const months = [
    { name: "January", value: 1 },
    { name: "February", value: 2 },
    { name: "March", value: 3 },
    { name: "April", value: 4 },
    { name: "May", value: 5 },
    { name: "June", value: 6 },
    { name: "July", value: 7 },
    { name: "August", value: 8 },
    { name: "September", value: 9 },
    { name: "October", value: 10 },
    { name: "November", value: 11 },
    { name: "December", value: 12 },
  ];

  const handleSelectChange = (value) => {
    setClassId(value || null);
  };

  const handleMonthClick = async (monthValue) => {
    if (!selectedYear) {
      message.error("Please select a year");
      return;
    }
    if (loadingMonth === monthValue) return;

    setSelectedMonth(monthValue);
    setLoadingMonth(monthValue);

    try {
      const params = {
        year: selectedYear,
        month: monthValue,
        ...(classId && { classId }),
      };

      const response = await axios({
        url: `${serverUrl}/admin/fee-template/export`,
        method: "GET",
        responseType: "blob",
        params,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });

      const monthName = months.find((m) => m.value === monthValue)?.name || monthValue;

      const selectedClassName = classId
        ? classes?.data?.find((c) => c._id === classId)?.name || classId
        : null;

      const fileName = classId
        ? `fee-report-${monthName}-class-${selectedClassName}-year-${selectedYear}.xlsx`
        : `fee-report-all-classes-${monthName}-${selectedYear}.xlsx`;

      saveAs(response.data, fileName);
      message.success("Report downloaded successfully");
    } catch (error) {
      console.error("Error:", error);
      message.error("Failed to download report");
    } finally {
      setLoadingMonth(null);
    }
  };

  async function fetchClasses() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }

  const classes = useQuery("classes", fetchClasses);

  return (
    <div>
      {" "}
      <div className="mt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full "
        >
          <div className="text-2xl text-orange-500 font-semibold pb-8">
            Report
          </div>

          <div className="flex space-x-3">
            {/* Class — Optional */}
            <div className="flex flex-col w-310">
              <h3 className="text-base pb-3">
                Class
                <span className="text-gray-400 text-sm font-normal ml-1">
                  (Optional)
                </span>
              </h3>
              <Select
                showSearch
                showArrow
                allowClear
                placeholder="All Classes"
                className="w-full bg-gray-300"
                style={{
                  border: "6px",
                  backgroundColor: "#EBEBEB",
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(value) => handleSelectChange(value)}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {classes?.isSuccess &&
                  classes?.data?.map((data) => {
                    return (
                      <Option key={data._id} value={data._id}>
                        {data?.name}
                      </Option>
                    );
                  })}
              </Select>
            </div>

            {/* Year — Required */}
            <div className="flex flex-col w-310">
              <h3 className="text-base pb-3">
                Year<span className="text-red-500 ml-1">*</span>
              </h3>
              <Select
                value={selectedYear}
                onChange={(val) => setSelectedYear(val)}
                className="w-full bg-gray-300"
                style={{
                  border: "6px",
                  backgroundColor: "#EBEBEB",
                }}
              >
                {academicYears.map((yr) => (
                  <Option key={yr} value={yr}>
                    {yr}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          {/* Info banner */}
          {!classId && (
            <div className="mt-3 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded px-3 py-2">
              No class selected — clicking a month will export paid fee records
              for <strong>all classes</strong> in that month.
            </div>
          )}

          <div className="grid grid-cols-4 gap-4 pt-4">
            {months.map((month) => {
              const isLoading = loadingMonth === month.value;
              const isDisabled = !selectedYear;
              return (
                <div
                  key={month.value}
                  onClick={() => !isDisabled && handleMonthClick(month.value)}
                  className={`p-4 border rounded transition-colors flex flex-col
                    ${
                      selectedMonth === month.value
                        ? "bg-orange-200 border-orange-400"
                        : "bg-white hover:bg-orange-100"
                    }
                    ${
                      isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }
                  `}
                >
                  <span className="flex justify-between items-center text-lg">
                    {month.name}
                    {isLoading ? (
                      <span className="text-sm text-orange-500 animate-pulse">
                        ...
                      </span>
                    ) : (
                      <AiOutlineDownload size={20} color="blue" />
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDetails;
